import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { DesktopOnlyButton as DesktopOnlyButtonBase } from 'pages/Common/styles/ControlBar';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { knowledgeCatalogQuery, knowledgeCatalogListQuery } from 'query/knowledge';
import { useToast } from '__components/ToastProvider';
import { getOriginalError } from '__services/AxiosService';
import { getErrorMessage } from '__hooks/useErrors';
import KnowledgeService from 'services/KnowledgeService';
import useCan from 'hooks/useCan';
import { isPluginCatalog } from 'pages/KnowledgeSettings/helpers';
import { DeleteCatalogModal } from '../Catalogs/DeleteCatalogModal';
import type { ReactNode } from 'react';

const DesktopOnlyButton = DesktopOnlyButtonBase as React.ComponentType<{
  color?: string;
  onClick?: () => void;
  'data-qa-id'?: string;
  children?: ReactNode;
  loading?: boolean;
}>;

export const DeleteCatalogButton = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { catalogId } = useParams<{ catalogId: string }>();
  const [showToast, toastVariant] = useToast();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const canWrite = useCan({
    edit: 'sections',
    for: 'settings_section.knowledgebase_setting',
  });

  const catalogQueryOptions = useMemo(
    () =>
      knowledgeCatalogQuery(catalogId, {
        staleTime: 0,
        enabled: !!catalogId,
      }),
    [catalogId]
  );

  const catalogListQueryOptions = useMemo(() => knowledgeCatalogListQuery({}), []);

  const { data: catalog } = useQuery(catalogQueryOptions);

  const { mutateAsync: deleteCatalogMutation } = useMutation({
    mutationFn: () => KnowledgeService.deleteKnowledgeCatalog(catalogId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogListQueryOptions.queryKey });
    },
  });

  const handleDeleteConfirmed = useCallback(async () => {
    try {
      await deleteCatalogMutation();
      setShowDeleteModal(false);
      history.push('/knowledge');
    } catch (error) {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('Failed to delete catalog'),
        variant: toastVariant.FAILURE,
      });
    }
  }, [deleteCatalogMutation, history, showToast, t, toastVariant]);

  if (!canWrite || !catalog || catalog.is_default || isPluginCatalog(catalog)) return null;

  return (
    <>
      <DesktopOnlyButton
        color="red"
        onClick={() => setShowDeleteModal(true)}
        data-qa-id="catalog-delete-button"
      >
        {t('Delete')}
      </DesktopOnlyButton>

      {showDeleteModal ? (
        <DeleteCatalogModal
          catalog={catalog}
          onConfirm={handleDeleteConfirmed}
          onHide={() => setShowDeleteModal(false)}
        />
      ) : null}
    </>
  );
};
